import { Point, Shape, ShapeType } from '@/types'

export const calculateAspectRatioFit = (
  srcWidth: number,
  srcHeight: number,
  maxWidth: number,
  maxHeight: number,
) => {
  const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight)
  return { width: srcWidth * ratio, height: srcHeight * ratio }
}

/**
 * Vérifie si un nombre est valide (c'est-à-dire ni NaN ni Infinity).
 * @param num Le nombre à vérifier
 * @returns true si le nombre est valide, false sinon
 */

export const isValidNumber = (value: number): boolean => {
  return !isNaN(value) && isFinite(value)
}

export const isNearFirstPoint = (point: Point, firstPoint: Point): boolean => {
  if (!point || !firstPoint) return false
  const distance = Math.sqrt(
    Math.pow(point.x - firstPoint.x, 2) + Math.pow(point.y - firstPoint.y, 2),
  )
  return distance < 8
}

export const calculateArea = (shape: Shape, pixelsPerMeter: number): number => {
  if (
    (shape.type !== ShapeType.FREEFORM && shape.type !== ShapeType.RECTANGLE) ||
    shape?.points?.length < 2
  ) {
    return 0
  }

  if (shape.type === ShapeType.RECTANGLE) {
    if (shape.points.length < 2) {
      return 0 // Retourne 0 si le rectangle n'est pas complet
    }
    const [p1, p2] = shape.points
    if (!p1 || !p2) {
      return 0
    }
    const width = Math.abs(p2.x - p1.x) / pixelsPerMeter
    const height = Math.abs(p2.y - p1.y) / pixelsPerMeter
    return width * height
  }

  // Pour les formes libres, utilisons la formule de l'aire de Gauss
  let area = 0
  const points = shape.points
  for (let i = 0; i < points.length; i++) {
    const j = (i + 1) % points.length
    area += points[i].x * points[j].y
    area -= points[j].x * points[i].y
  }
  area = Math.abs(area) / 2
  return area / (pixelsPerMeter * pixelsPerMeter)
}

export const formatArea = (area: number): string => {
  return `${area.toFixed(2)} m²`
}

/**
 * Calcule la distance euclidienne entre deux points.
 * @param p1 Le premier point
 * @param p2 Le second point
 * @returns La distance entre les deux points
 */
export const calculateDistance = (p1: Point, p2: Point): number => {
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  return Math.sqrt(dx * dx + dy * dy)
}

export const calculateAngle = (start: Point, end: Point): number => {
  return Math.atan2(end.y - start.y, end.x - start.x)
}

export const calculateMidpoint = (start: Point, end: Point): Point => {
  return { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 }
}
